import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initial = user?.name?.charAt(0).toUpperCase() ?? '?';

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  const items = [
    { label: 'Profile', icon: User, action: () => { navigate('/profile'); setOpen(false); } },
    ...(isAdminOrManager ? [{ label: 'Settings', icon: Settings, action: () => { navigate('/settings'); setOpen(false); } }] : []),
    { divider: true },
    { label: 'Logout', icon: LogOut, action: () => { logout(); setOpen(false); }, danger: true },
  ] as const;

  return (
    <div ref={ref} className="relative">
      {/* Avatar trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        className={`h-9 w-9 rounded-full bg-[#6366F1] flex items-center justify-center text-sm font-semibold text-white transition-opacity hover:opacity-90 ${open ? 'ring-2 ring-[#6366F1]/40 ring-offset-1' : ''}`}
      >
        {initial}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden z-50"
          style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.10)' }}
        >
          {/* User info header */}
          <div className="px-4 py-3 border-b border-gray-50">
            <p className="text-sm font-semibold text-[#1E293B] truncate">{user?.name}</p>
            <p className="text-xs text-[#64748B] capitalize truncate">{user?.role}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {items.map((item, i) => {
              if ('divider' in item) {
                return <div key={i} className="my-1 border-t border-gray-100" />;
              }
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  onClick={item.action}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left
                    ${'danger' in item && item.danger
                      ? 'text-red-500 hover:bg-red-50'
                      : 'text-[#374151] hover:bg-gray-50 hover:text-[#6366F1]'
                    }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
